import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useStore } from '@nanostores/react';
import { Loader2 } from 'lucide-react';
import { firebaseConnector } from '../../services/FirebaseConnector';

/**
 * THe login page which displays the options for performing login.
 */
export const LoginPage = () => {
  // state for loading google and microsoft login
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  async function handleLoginGoogle() {
    setIsGoogleLoading(true);
    await firebaseConnector.loginGoogle();
    setIsGoogleLoading(false);
  }

  async function handleLoginMicrosoft() {
    setIsMicrosoftLoading(true);
    await firebaseConnector.loginMicrosoft();
    setIsMicrosoftLoading(false);
  }

  // loader state for profile
  const profileLoader = useStore($profileLoader);

  return (
    <>
      {profileLoader ? (
        // Show a loading spinner while we wait for the profile to load
        <div className="z-50 flex h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        // Show the login page
        <div className="lg:mt-20 lg:p-8">
          <div className="mx-auto w-[400px]">
            <img
              className="mx-auto h-10 w-auto"
              src="/tennis-logo.png"
              alt="Your Company"
            />
            <h2 className="mt-6 text-center text-2xl  leading-9 tracking-tight text-foreground">
              <span className="font-bold">Rally</span> by Tennis Finance
            </h2>
          </div>

          <Card className="mx-auto mt-10 w-[400px]">
            <CardHeader>
              <CardTitle className=" text-center">
                Sign in to your account
              </CardTitle>
              <CardDescription className=" text-center">
                Use your Google or Microsoft account to sign in.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  type="button"
                  disabled={isGoogleLoading}
                  onClick={handleLoginGoogle}
                >
                  {isGoogleLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <svg
                      aria-hidden="true"
                      className="mr-2 h-5 w-5"
                      viewBox="0 0 48 48"
                    >
                      <path
                        fill="#FFC107"
                        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                      ></path>
                      <path
                        fill="#FF3D00"
                        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                      ></path>
                      <path
                        fill="#4CAF50"
                        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                      ></path>
                      <path
                        fill="#1976D2"
                        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                      ></path>
                    </svg>
                  )}
                  Google
                </Button>

                <Button
                  variant="outline"
                  type="button"
                  size="lg"
                  disabled={isMicrosoftLoading}
                  onClick={handleLoginMicrosoft}
                >
                  {isMicrosoftLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <svg
                      aria-hidden="true"
                      className="mr-2 h-5 w-5"
                      viewBox="0 0 48 48"
                    >
                      <path
                        fill="#ff5722"
                        d="M6 6H22V22H6z"
                        transform="rotate(-180 14 14)"
                      ></path>
                      <path
                        fill="#4caf50"
                        d="M26 6H42V22H26z"
                        transform="rotate(-180 34 14)"
                      ></path>
                      <path
                        fill="#ffc107"
                        d="M26 26H42V42H26z"
                        transform="rotate(-180 34 34)"
                      ></path>
                      <path
                        fill="#03a9f4"
                        d="M6 26H22V42H6z"
                        transform="rotate(-180 14 34)"
                      ></path>
                    </svg>
                  )}
                  Microsoft
                </Button>
              </div>
            </CardContent>
            <CardFooter className="">
              <p className="mt-4 px-8 text-center text-sm text-muted-foreground">
                By clicking continue, you agree to our{' '}
                <a
                  href="https://www.tennisfinance.com/company/terms-and-conditions"
                  target="_blank"
                  className="underline underline-offset-4 hover:text-primary"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://www.tennisfinance.com/company/privacy-policy"
                  target="_blank"
                  className="underline underline-offset-4 hover:text-primary"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </CardFooter>
          </Card>
        </div>
      )}
    </>
  );
};
